import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Button, Row, Col, Statistic, Timeline, Table, Divider, Tag, Space, Select, DatePicker, Spin, message } from 'antd';
import { ArrowLeftOutlined, BellOutlined, InfoCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import Box from '@iso/components/utility/box';
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import SensorDetailPage from "./SensorDetail.styles";
import moment from 'moment-timezone';
import EditSensorNameModal from './EditSensorNameModal';
import { deviceSensorService } from '../../../services/deviceSensor.service';
import { userService, sensorService } from '@iso/services';
import ActivityMap from '../Activity/ActivityMap';
import DoorSensorDetail from './DoorSensorDetail';
import AiMeterReaderDetail from './AiMeterReaderDetail';
import { WaterDropIcon } from './SensorVisualisations';
import { extractPulseEntries, calculatePerReadingDeltas, getSensorTypeLabel } from './doorSensorUtils';
import {
  SENSOR_TYPE_AI_METER_READER,
  SENSOR_TYPE_DOOR_CONTACT_SENSOR,
  SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER,
} from '@iso/constants/trackpacSensorTypes';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    Rectangle
} from "recharts";
import {
  Temperature,
  Humidity,
  Pressure,
  Distance,
  AirQuality,
  Light,
  UV,
  Weather,
  Visibility,
  Electricity,
  Vibration,
  Relay,
  DefaultSensor
} from './sensorIcons'

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const SensorDetail = ({ sensor: initialSensor, onBack, timezone = 'UTC', isModal = false }) => {
    const [sensor, setSensor] = useState(initialSensor);
    const [selectedSensorType, setSelectedSensorType] = useState('');
    const [selectedTimezone, setSelectedTimezone] = useState(timezone);
    // Pulse-counter variants (door / water) default to 'today' so the top-level
    // Select label matches what DoorSensorDetail loads on mount via its own
    // handleDoorTimeRangeChange('today') init effect. Without this they'd
    // drift — Select reads "Last 24 hours" while the chart shows today's data.
    // Other sensor types keep the legacy 'last_24_hours' default.
    const initialPulseVariant =
        initialSensor?.part_number === SENSOR_TYPE_DOOR_CONTACT_SENSOR
        || initialSensor?.part_number === SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER;
    const [timelineOption, setTimelineOption] = useState(
        initialPulseVariant ? 'today' : 'last_24_hours'
    );
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [editSensorNameModalVisible, setEditSensorNameModalVisible] = useState(false);
    const [editSensor, setEditSensor] = useState({});
    const [statsSensor, setStatsSensor] = useState({});
    const [typeModalSensor, setTypeModalSensor] = useState('edit');
    const [usersProperty, setUsersProperty] = useState([]);
    const [clickedSensorType, setClickedSensorType] = useState('');
    
    // New states for ActivityMap integration
    const [breadcrumbData, setBreadcrumbData] = useState([]);
    const [filteredBreadcrumbData, setFilteredBreadcrumbData] = useState([]);
    const [sliderRange, setSliderRange] = useState([0, 0]);
    
    const dateFormat = 'DD/MM/YYYY HH:mm:ss';
    const rangePickerDateFormat = 'DD/MM/YYYY';
    const activePropertyId = useSelector(
        (state) => state.property.activePropertyId
    );

    const getUsers = (propertyId) => {
        userService.getUserForProperty(propertyId).then(res => {
            if(res.code === '0000'){
                setUsersProperty(res.users);
            }
        });
    };

    useEffect(() => {
        if (activePropertyId) {
            getUsers(activePropertyId);
        }
    }, [activePropertyId]);

    // Check if this is an asset tracking sensor
    const isAssetTrackingSensor = () => {
        return sensor?.part_number === "ASSET_TRACKING";
    };

    // Check if this is a door contact sensor (counter variant)
    const isDoorContactSensor = sensor?.part_number === SENSOR_TYPE_DOOR_CONTACT_SENSOR;

    // Same CPL03 hardware but wired as a water-meter pulse counter.
    const isWaterMeterSensor = sensor?.part_number === SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER;

    // Both variants share the same DoorSensorDetail UI shell — used to keep
    // the hide-timeline-option logic uniform below.
    const isPulseCounterVariant = isDoorContactSensor || isWaterMeterSensor;

    // Check if this is a Dragino AIS01-LB AI meter reader
    const isAiMeterReader = sensor?.part_number === SENSOR_TYPE_AI_METER_READER;

    const convertTimelineToBreadcrumbs = useCallback((sensorData) => {
        if (!sensorData.timelines || !sensorData.timelines.length) {
            return [];
        }

        const breadcrumbs = [];
        sensorData.timelines.forEach(timeline => {
            const lat = timeline.latitude?.value;
            const lng = timeline.longitude?.value;
            const time = timeline.latitude?.time || timeline.longitude?.time;
            
            if (lat && lng && lat !== 0 && lng !== 0 && time) {
                breadcrumbs.push({
                    lat: parseFloat(lat),
                    lng: parseFloat(lng),
                    time: time,
                    name: sensorData.asset?.title || sensorData.name || 'Asset Location'
                });
            }
        });

        // Sort by time (assuming time format is DD-MM-YYYY HH:mm)
        breadcrumbs.sort((a, b) => {
            const timeA = moment(a.time, 'DD-MM-YYYY HH:mm');
            const timeB = moment(b.time, 'DD-MM-YYYY HH:mm');
            return timeA.valueOf() - timeB.valueOf();
        });

        return breadcrumbs;
    }, []);

    const handleSliderChange = useCallback((range) => {
        setSliderRange(range);
        if (breadcrumbData.length > 0) {
            const startIndex = range[0];
            const endIndex = range[1] + 1; // +1 because slice end is exclusive
            const filtered = breadcrumbData.slice(startIndex, endIndex);
            setFilteredBreadcrumbData(filtered);
        }
    }, [breadcrumbData]);

    // Updated function to use getDetail with sensor.device_id
    const fetchSensorData = useCallback(async (sensorId, deviceId, start_date, end_date, timezone) => {
        try {
            setLoading(true);
            const params = { 
                start_date: moment(start_date, dateFormat).format('DD/MM/YYYY HH:mm:ss'),
                end_date: moment(end_date, dateFormat).format('DD/MM/YYYY HH:mm:ss'),
                sensor_id: sensorId,
                timezone: timezone
            };

            const response = await deviceSensorService.getDetail(deviceId, params);
            
            if (response && response.device) {
                let deviceData;
                if (Array.isArray(response.device)) {
                    deviceData = response.device.find(d => d.id === parseInt(deviceId)) || response.device[0];
                } else {
                    deviceData = response.device;
                }
                
                if (deviceData && deviceData.sensors && deviceData.sensors.length > 0) {
                    const targetSensor = deviceData.sensors.find(s => s.id === parseInt(sensorId));
                    
                    if (targetSensor) {
                        setSensor(prevSensor => ({
                            ...prevSensor,
                            ...targetSensor,
                            device_id: targetSensor.device_id || prevSensor.device_id,
                            asset: targetSensor.asset || prevSensor.asset,
                            timelines: targetSensor.timelines || [],
                            stats: targetSensor.stats || {}
                        }));

                        // If this is an asset tracking sensor, convert timeline to breadcrumb data
                        if (targetSensor.part_number === "ASSET_TRACKING") {
                            const breadcrumbs = convertTimelineToBreadcrumbs(targetSensor);
                            setBreadcrumbData(breadcrumbs);
                            if (breadcrumbs.length > 0) {
                                setFilteredBreadcrumbData(breadcrumbs);
                                const maxIndex = Math.max(0, breadcrumbs.length - 1);
                                setSliderRange([0, maxIndex]);
                            } else {
                                setFilteredBreadcrumbData([]);
                                setSliderRange([0, 0]);
                            }
                        } else {
                            // Clear breadcrumb data for non-asset-tracking sensors
                            setBreadcrumbData([]);
                            setFilteredBreadcrumbData([]);
                            setSliderRange([0, 0]);
                        }
                    } else {
                        console.warn('Target sensor not found in response');
                        if (deviceData.sensors[0]) {
                            const firstSensor = deviceData.sensors[0];
                            setSensor(prevSensor => ({
                                ...prevSensor,
                                ...firstSensor,
                                timelines: firstSensor.timelines || [],
                                stats: firstSensor.stats || {}
                            }));
                            
                            // Handle asset tracking for first sensor fallback
                            if (firstSensor.part_number === "ASSET_TRACKING") {
                                const breadcrumbs = convertTimelineToBreadcrumbs(firstSensor);
                                setBreadcrumbData(breadcrumbs);
                                if (breadcrumbs.length > 0) {
                                    setFilteredBreadcrumbData(breadcrumbs);
                                    const maxIndex = Math.max(0, breadcrumbs.length - 1);
                                    setSliderRange([0, maxIndex]);
                                } else {
                                    setFilteredBreadcrumbData([]);
                                    setSliderRange([0, 0]);
                                }
                            } else {
                                setBreadcrumbData([]);
                                setFilteredBreadcrumbData([]);
                                setSliderRange([0, 0]);
                            }
                        } else {
                            message.warning('No sensor found in response');
                            setBreadcrumbData([]);
                            setFilteredBreadcrumbData([]);
                            setSliderRange([0, 0]);
                        }
                    }
                } else {
                    console.warn('No sensors found in device response');
                    message.warning('No sensor data found for the selected time range');
                    setBreadcrumbData([]);
                    setFilteredBreadcrumbData([]);
                    setSliderRange([0, 0]);
                }
            } else {
                console.warn('Invalid response structure:', response);
                message.error('Failed to fetch sensor data - invalid response structure');
                setBreadcrumbData([]);
                setFilteredBreadcrumbData([]);
                setSliderRange([0, 0]);
            }
        } catch (error) {
            console.error('Error fetching sensor data:', error);
            message.error(`Failed to fetch sensor data: ${error.message || 'Unknown error'}`);
            setBreadcrumbData([]);
            setFilteredBreadcrumbData([]);
            setSliderRange([0, 0]);
        } finally {
            setLoading(false);
        }
    }, [convertTimelineToBreadcrumbs]);

    useEffect(() => {
        const defaultStartDate = moment().tz(selectedTimezone).subtract(23, 'hours').subtract(59, 'minutes').format(dateFormat);
        const defaultEndDate = moment().tz(selectedTimezone).format(dateFormat);
        setStartDate(defaultStartDate);
        setEndDate(defaultEndDate);
    }, [selectedTimezone]);

    useEffect(() => {
        if (sensor && sensor.timelines) {
            const sensorTypes = getSensorTypes(sensor);
            if (sensorTypes.length > 0 && !selectedSensorType) {
                setSelectedSensorType(sensorTypes[0]);
            }
        }
    }, [sensor, selectedSensorType]);

    // FIXED: Updated useEffect with proper dependencies and logic
    useEffect(() => {
        if (sensor && sensor.id && sensor.device_id && startDate && endDate) {
            // Always fetch data when dates change
            fetchSensorData(sensor.id, sensor.device_id, startDate, endDate, selectedTimezone);
        }
    }, [sensor?.id, sensor?.device_id, startDate, endDate, selectedTimezone, fetchSensorData]);

    // FIXED: Additional useEffect to handle breadcrumb data updates and ensure proper clearing
    useEffect(() => {
        if (sensor && isAssetTrackingSensor() && sensor.timelines) {
            const breadcrumbs = convertTimelineToBreadcrumbs(sensor);
            setBreadcrumbData(breadcrumbs);
            setFilteredBreadcrumbData(breadcrumbs);
            setSliderRange([0, Math.max(0, breadcrumbs.length - 1)]);
        } else if (sensor && !isAssetTrackingSensor()) {
            // Clear breadcrumb data for non-asset-tracking sensors
            setBreadcrumbData([]);
            setFilteredBreadcrumbData([]);
            setSliderRange([0, 0]);
        }
    }, [sensor?.timelines, sensor?.part_number, convertTimelineToBreadcrumbs]);

    // FIXED: Force clear and refresh breadcrumb data when dates change for asset tracking sensors
    useEffect(() => {
        if (isAssetTrackingSensor() && startDate && endDate) {
            setBreadcrumbData([]);
            setFilteredBreadcrumbData([]);
            setSliderRange([0, 0]);
        }
    }, [startDate, endDate]);

    // Stable callback for DoorSensorDetail to update date range without causing re-render loops
    const handleDoorDateRangeChange = useCallback((start, end) => {
        setStartDate(start);
        setEndDate(end);
    }, []);

    const capitalizeFirstLetter = (string) => {
        if (!string) return '';
        return getSensorTypeLabel(string);
    };

    const handleEditSensor = (sensor, sensorType, typeModal) => {
        setEditSensor(sensor);
        setStatsSensor(typeModal === 'alarm' ? sensor?.stats?.[sensorType] : {});
        setTypeModalSensor(typeModal);
        setClickedSensorType(sensorType);
        setEditSensorNameModalVisible(true);
    };

    const cancelEditSensorNameModal = () => {
        setEditSensorNameModalVisible(false);
    };

    const onEditSensorNameSaved = (value) => {
        const params = {
            sensor: {
                name: value
            }
        };
        sensorService.storeOrUpdate(params, editSensor.id).then(res => {
            if (res.code === '0000') {
                setSensor(prev => ({ ...prev, name: value }));
                setEditSensorNameModalVisible(false);
            }
        });
    };

    const getSensorTypes = (sensor) => {
        if (!sensor.timelines || !sensor.timelines.length) {
            return [];
        }

        const types = new Set();
        sensor.timelines.forEach(timeline => {
            Object.keys(timeline).forEach(key => {
                if (key !== 'time') {
                    types.add(key);
                }
            });
        });
        return Array.from(types);
    };

    const getLastKnownValue = (sensor, sensorType) => {
        if (!sensor.timelines || !sensor.timelines.length || !sensorType) {
            return null;
        }

        for (let i = sensor.timelines.length - 1; i >= 0; i--) {
            const timeline = sensor.timelines[i];
            if (timeline[sensorType]) {
                return {
                    value: timeline[sensorType].value,
                    unit: timeline[sensorType].unit || '',
                    time: timeline[sensorType].time
                };
            }
        }
        return null;
    };

    const getSensorTypeIcon = (sensorType, className = "sidebar-icon") => {
        const iconMap = {
            temperature: Temperature,
            humidity: Humidity,
            moisture: Humidity,
            pressure: Pressure,
            distance: Distance,
            air_quality: AirQuality,
            light: Light,
            uv: UV,
            weather: Weather,
            visibility: Visibility,
            height: Distance,
            default: DefaultSensor,
            conductivity: Electricity,
            voltage: Electricity,
            vibration: Vibration,
            relay: Relay,
            latitude: Distance,
            longitude: Distance,
            default: DefaultSensor
        };
        
        const IconComponent = iconMap[sensorType.toLowerCase()] || iconMap.default;
        return <IconComponent className={className} />;
    };

    const getSensorTypeStats = (sensor, sensorType) => {
        if (!sensor.timelines || !sensorType) return null;

        const values = sensor.timelines
            .filter(timeline => timeline[sensorType])
            .map(timeline => parseFloat(timeline[sensorType].value) || 0);

        if (values.length === 0) return null;

        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

        return { min, max, avg: avg.toFixed(2) };
    };


    const getTimelineTableData = (sensor) => {
        if (!sensor.timelines) return [];

        return sensor.timelines
            .slice()
            .reverse()
            .slice(0, isModal ? 20 : 50) // Show fewer rows in modal
            .map((timeline, index) => {
                const entry = { key: index };
                
                let timeValue = timeline.time;
                if (!timeValue) {
                    Object.keys(timeline).forEach(key => {
                        if (key !== 'time' && timeline[key] && timeline[key].time && !timeValue) {
                            timeValue = timeline[key].time;
                        }
                    });
                }
                entry.time = timeValue;
                
                Object.keys(timeline).forEach(key => {
                    if (key !== 'time' && timeline[key]) {
                        entry[key] = `${timeline[key].value}${timeline[key].unit || ''}`;
                        // Carry the raw numeric value alongside the formatted
                        // string so renderers that need to do arithmetic
                        // (e.g. Total Volume column for water meters) don't
                        // have to re-parse the "<n><unit>" display string.
                        entry[`${key}_raw`] = timeline[key].value;
                    }
                });
                
                return entry;
            });
    };

    const getTimelineTableColumns = (sensor) => {
        const sensorTypes = getSensorTypes(sensor);
        const columns = [
            {
                title: 'Time',
                dataIndex: 'time',
                key: 'time',
                width: isModal ? 120 : 150,
                render: (text) => {
                    const parsedDate = moment(text, 'DD-MM-YYYY HH:mm');
                    return parsedDate.isValid() ? parsedDate.format('DD/MM HH:mm') : text;
                },
                sorter: (a, b) => {
                    const dateA = moment(a.time, 'DD-MM-YYYY HH:mm');
                    const dateB = moment(b.time, 'DD-MM-YYYY HH:mm');
                    return dateA.valueOf() - dateB.valueOf();
                },
            }
        ];

        sensorTypes.forEach(sensorType => {
            columns.push({
                title: isModal ? capitalizeFirstLetter(sensorType) : (
                    <span>
                        {getSensorTypeIcon(sensorType)} {capitalizeFirstLetter(sensorType)}
                    </span>
                ),
                dataIndex: sensorType,
                key: sensorType,
                render: (text) => text || '-'
            });
        });

        // For the water-meter variant, append a "Total Volume" column that
        // converts each row's cumulative `total_pulses` into m³ using the
        // sensor's per-meter `litres_per_pulse` ratio. Mirrors how the door
        // sensor surfaces its cumulative count, but in the user's natural
        // unit (m³ instead of raw pulses).
        if (sensor?.part_number === SENSOR_TYPE_DOOR_CONTACT_SENSOR_WATER) {
            const litresPerPulse = Number(sensor.litres_per_pulse) > 0
                ? Number(sensor.litres_per_pulse)
                : 1;
            columns.push({
                title: isModal ? 'Total Volume' : (
                    // Inline WaterDropIcon — getSensorTypeIcon has no
                    // 'water_volume' mapping and would silently render the
                    // generic DefaultSensor blob.
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        <WaterDropIcon size={14} /> Total Volume
                    </span>
                ),
                key: 'total_volume_m3',
                render: (_, record) => {
                    // Prefer the raw numeric value carried by getTimelineTableData
                    // (no string parsing needed); fall back to the formatted
                    // string for resilience.
                    const raw = record.total_pulses_raw !== undefined
                        ? record.total_pulses_raw
                        : record.total_pulses;
                    if (raw === undefined || raw === null || raw === '') return '-';
                    // parseFloat stops at the first non-numeric character, so
                    // it cleanly handles both a Number and a string like "180count".
                    const pulses = parseFloat(String(raw));
                    if (!Number.isFinite(pulses)) return '-';
                    const m3 = (pulses * litresPerPulse) / 1000;
                    // Cap at 3 decimals but strip trailing zeros — "0" not "0.000".
                    return `${String(Number(m3.toFixed(3)))} m³`;
                },
            });
        }

        return columns;
    };

    const renderStatsChart = (sensor, sensorType) => {
        if (!sensor.stats || !sensor.stats[sensorType]) return null;

        const stat = sensor.stats[sensorType];
        let [minVal, maxVal] = stat.unit === "mm" 
            ? [stat.max, stat.min] 
            : [stat.min, stat.max];

        const barChartData = [
            { label: 'Avg', value: stat.avg },
            { label: 'Min', value: minVal },
            { label: 'Max', value: maxVal }
        ];

        const unit = stat.unit || '';

        return (
            <div style={{marginBottom: '30px'}}>
                <ResponsiveContainer width="100%" height={isModal ? 300 : 400}>
                    <BarChart data={barChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis unit={unit} />
                        <Tooltip formatter={(value) => `${value}${unit}`} />
                        <Legend />
                        <Bar
                            name={stat.label || capitalizeFirstLetter(sensorType)}
                            dataKey="value"
                            fill="#0B2A1B"
                            activeBar={<Rectangle fill="pink" stroke="blue" />}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };


    const renderTimelineChart = (sensor, sensorType) => {
        if (!sensor.timelines || !sensor.timelines.length) return null;

        // For door sensors showing door_status, show per-reading pulse deltas instead
        if (sensor.part_number === 'DOOR_CONTACT_SENSOR' && sensorType === 'door_status') {
            const pulseEntries = extractPulseEntries(sensor.timelines);
            const deltaData = calculatePerReadingDeltas(pulseEntries);

            if (!deltaData.length) return null;

            return (
                <div style={{marginBottom: '30px'}}>
                    <ResponsiveContainer width="100%" height={isModal ? 300 : 400}>
                        <LineChart data={deltaData} margin={{ left: 20, right: 10, top: 5, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="time" />
                            <YAxis width={80} domain={['auto', 'auto']} />
                            <Tooltip formatter={(value) => [`${value} pulses`, 'Pulses per Reading']} />
                            <Legend />
                            <Line
                                name="Pulses per Reading"
                                type="monotone"
                                dataKey="value"
                                stroke="#0B2A1B"
                                dot={{ r: 2 }}
                                activeDot={{ r: 6 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            );
        }

        const sensorData = sensor.timelines
            .filter(timeline => timeline[sensorType])
            .map(timeline => timeline[sensorType]);

        if (!sensorData.length) return null;

        return (
            <div style={{marginBottom: '30px'}}>
                <ResponsiveContainer width="100%" height={isModal ? 300 : 400}>
                    <LineChart data={sensorData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="time" />
                        <YAxis
                            width={80}
                            unit={sensorData[0]?.unit || ''}
                            reversed={sensor.part_number === "DISTANCE_DETECTION_SENSOR"}
                            domain={['auto', 'auto']}
                        />
                        <Tooltip formatter={(value) => `${value}${sensorData[0]?.unit || ''}`} />
                        <Legend />
                        <Line
                            name={capitalizeFirstLetter(sensorType)}
                            type="monotone"
                            dataKey="value"
                            stroke="#0B2A1B"
                            activeDot={{ r: 8 }}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const getTimelineOptions = () => {
        return [
            { value: 'last_24_hours', label: 'Last 24 hours' },
            { value: 'today', label: 'Today' },
            { value: 'yesterday', label: 'Yesterday' },
            { value: 'last_7_days', label: 'Last 7 days' },
            { value: 'last_30_days', label: 'Last 30 days' },
            { value: 'this_month', label: 'This month' },
            { value: 'last_month', label: 'Last month' },
            { value: 'custom', label: 'Custom' },
        ];
    };

    const timelineOptions = {
        last_24_hours: {
            startDate: moment().tz(selectedTimezone).subtract(23, 'hours').subtract(59, 'minutes'),
            endDate: moment().tz(selectedTimezone)
        },
        today: {
            startDate: moment().tz(selectedTimezone).startOf('day'),
            endDate: moment().tz(selectedTimezone).endOf('day')
        },
        yesterday: {
            startDate: moment().tz(selectedTimezone).subtract(1, 'days').startOf('day'),
            endDate: moment().tz(selectedTimezone).subtract(1, 'days').endOf('day')
        },
        last_7_days: {
            startDate: moment().tz(selectedTimezone).subtract(7, 'days'),
            endDate: moment().tz(selectedTimezone)
        },
        last_30_days: {
            startDate: moment().tz(selectedTimezone).subtract(30, 'days'),
            endDate: moment().tz(selectedTimezone)
        },
        this_month: {
            startDate: moment().tz(selectedTimezone).startOf('month'),
            endDate: moment().tz(selectedTimezone).endOf('month')
        },
        last_month: {
            startDate: moment().tz(selectedTimezone).subtract(1, 'month').startOf('month'),
            endDate: moment().tz(selectedTimezone).subtract(1, 'month').endOf('month')
        }
    };

    const onTimelineOptionChange = (value) => {
        setTimelineOption(value);

        if (value !== 'custom' && timelineOptions[value]) {
            const { startDate: start, endDate: end } = timelineOptions[value];
            setStartDate(start.format(dateFormat));
            setEndDate(end.format(dateFormat));
        }
    };

    const handleDateChange = (date, dateString) => {
        if (date && date[0] && date[1]) {
            setStartDate(date[0].startOf('day').format(dateFormat));
            setEndDate(date[1].endOf('day').format(dateFormat));
        }
    };

    const renderTimezoneSelect = () => {
        const commonTimezones = [
            'UTC',
            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Los_Angeles',
            'Europe/London',
            'Europe/Paris',
            'Asia/Tokyo',
            'Australia/Sydney'
        ];

        return (
            <Select
                style={{ width: isModal ? 120 : 170, marginRight: 10 }}
                defaultValue={selectedTimezone}
                value={selectedTimezone}
                onChange={setSelectedTimezone}
                showSearch
                placeholder="Select timezone"
                size={isModal ? 'small' : 'middle'}
            >
                {commonTimezones.map(tz => (
                    <Option key={tz} value={tz}>{tz}</Option>
                ))}
            </Select>
        );
    };

    if (!sensor) {
        return (
            <LayoutWrapper>
                <Box>
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Text>No sensor data available</Text>
                        <br />
                        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={onBack}>
                            Back to Sensors
                        </Button>
                    </div>
                </Box>
            </LayoutWrapper>
        );
    }

    const sensorTypes = getSensorTypes(sensor);
    const lastValue = getLastKnownValue(sensor, selectedSensorType);
    const timelineData = getTimelineTableData(sensor);
    const timelineColumns = getTimelineTableColumns(sensor);

    // Modal-specific styles
    const containerStyle = isModal ? {
        padding: '16px',
        maxHeight: '80vh',
        overflowY: 'auto'
    } : {
        padding: '20px'
    };

    const cardStyle = isModal ? {
        marginBottom: '16px'
    } : {
        marginBottom: '24px'
    };

    return (
        <LayoutWrapper>
            <SensorDetailPage className={isModal ? 'modal-view' : ''}>
                <Box>
                    <Spin spinning={loading}>
                        <div style={containerStyle}>
                            {/* Header Section */}
                            <div style={{ marginBottom: isModal ? '16px' : '24px' }}>
                                <Button 
                                    type="primary" 
                                    icon={<ArrowLeftOutlined />} 
                                    onClick={onBack}
                                    style={{ marginBottom: '12px' }}
                                    size={isModal ? 'small' : 'middle'}
                                >
                                    {isModal ? 'Back' : 'Back to Sensors'}
                                </Button>
                                
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: isModal ? 'center' : 'flex-start',
                                    flexDirection: isModal ? 'column' : 'row',
                                    gap: isModal ? '12px' : '0'
                                }}>
                                    <div style={{ width: isModal ? '100%' : 'auto' }}>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
                                            <Title level={isModal ? 3 : 2} style={{ margin: 0 }}>
                                                {sensor.name || `Sensor ${sensor.id}`}
                                            </Title>
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={<EditOutlined />}
                                                onClick={() => handleEditSensor(sensor, '', 'edit')}
                                                style={{
                                                    color: '#0B2A1B',
                                                    fontSize: '14px',
                                                    height: '24px',
                                                    width: '24px',
                                                    padding: '0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    transition: 'all 0.2s ease',
                                                    marginTop: '2px'
                                                }}
                                                title="Edit sensor name"
                                            />
                                        </div>
                                        {sensor.asset && (
                                            <div style={{ marginTop: '8px' }}>
                                                <Tag color="gray" size={isModal ? 'small' : 'default'}>
                                                    <InfoCircleOutlined /> Asset: {sensor.asset.title}
                                                </Tag>
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Date Range Controls */}
                                    <div className="button-group" style={{
                                        display: 'flex',
                                        flexWrap: isModal ? 'wrap' : 'nowrap',
                                        gap: isModal ? '8px' : '0',
                                        width: isModal ? '100%' : 'auto',
                                        justifyContent: isModal ? 'space-between' : 'flex-end'
                                    }}>
                                        {renderTimezoneSelect()}
                                        {/*
                                          Timeline preset + Custom RangePicker apply to door/water
                                          pulse-counter variants too — stakeholders need an explicit
                                          calendar option alongside DoorSensorDetail's preset summary
                                          cards (Today / Yesterday / Weekly / Daily Average). Both
                                          paths fan into the same fetchSensorData via
                                          onDateRangeChange, so they coexist without conflict. AI
                                          meter reader stays hidden — it owns its own detail UI.
                                        */}
                                        {!isAiMeterReader && (
                                            <>
                                                <Select
                                                    style={{ width: isModal ? 100 : 140 }}
                                                    defaultValue={timelineOption}
                                                    value={timelineOption}
                                                    onChange={onTimelineOptionChange}
                                                    size={isModal ? 'small' : 'middle'}
                                                >
                                                    {getTimelineOptions().map((option) => (
                                                        <Option key={option.value} value={option.value}>{option.label}</Option>
                                                    ))}
                                                </Select>

                                                {timelineOption === 'custom' && (
                                                    <RangePicker
                                                        format={rangePickerDateFormat}
                                                        onChange={handleDateChange}
                                                        size={isModal ? 'small' : 'middle'}
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Render ActivityMap for Asset Tracking sensors */}
                            {isAssetTrackingSensor() ? (
                            <Row gutter={[isModal ? 16 : 24, isModal ? 16 : 24]}>
                                <Col span={24}>
                                    <Card style={cardStyle}>
                                        <div 
                                            className="map-container"
                                            style={{ 
                                                height: isModal ? '450px' : '600px', 
                                                position: 'relative',
                                                marginBottom: '0'
                                            }}
                                        >
                                            <ActivityMap 
                                                breadcrumbData={breadcrumbData}
                                                loading={loading}
                                                onSliderChange={handleSliderChange}
                                                sliderRange={sliderRange}
                                                filteredBreadcrumbData={filteredBreadcrumbData}
                                                isEmbedded={true}
                                                key={`sensor-${sensor.id}-${startDate}-${endDate}-${breadcrumbData.length}`}
                                            />
                                        </div>
                                        {breadcrumbData.length === 0 && !loading && (
                                            <div style={{ 
                                                position: 'absolute', 
                                                top: '50%', 
                                                left: '50%', 
                                                transform: 'translate(-50%, -50%)',
                                                textAlign: 'center',
                                                zIndex: 1000,
                                                background: 'rgba(255,255,255,0.9)',
                                                padding: '20px',
                                                borderRadius: '8px'
                                            }}>
                                                <Text type="secondary">No location data available for the selected time range</Text>
                                            </div>
                                        )}
                                    </Card>
                                </Col>
                                    
                                    {/* Timeline Table for Asset Tracking */}
                                    <Col span={24}>
                                        <Card title="Recent Location Data">
                                            <Table
                                                dataSource={timelineData}
                                                columns={timelineColumns}
                                                pagination={{
                                                    pageSize: isModal ? 5 : 10,
                                                    showSizeChanger: !isModal,
                                                    showQuickJumper: !isModal,
                                                    showTotal: (total, range) => 
                                                        `${range[0]}-${range[1]} of ${total} readings`
                                                }}
                                                scroll={{ x: true }}
                                                size="small"
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            ) : isPulseCounterVariant ? (
                                // Same DoorSensorDetail shell for both door counter
                                // and water-meter variants — the water variant
                                // passes display props (label, unit, value
                                // transform, icon) that turn the chart axis,
                                // tooltip, summary unit label, and title into m³.
                                <DoorSensorDetail
                                    sensor={sensor}
                                    selectedTimezone={selectedTimezone}
                                    isModal={isModal}
                                    cardStyle={cardStyle}
                                    dateFormat={dateFormat}
                                    onDateRangeChange={handleDoorDateRangeChange}
                                    timelineData={timelineData}
                                    timelineColumns={timelineColumns}
                                    {...(isWaterMeterSensor ? {
                                        displayLabel: 'Usage',
                                        unit: 'm³',
                                        valueTransform: (pulses) => {
                                            // m³ = pulses × litres_per_pulse / 1000.
                                            // Fall back to 1 L/pulse (typical residential)
                                            // when the per-sensor setting is missing / invalid.
                                            const litresPerPulse = Number(sensor?.litres_per_pulse) > 0
                                                ? Number(sensor.litres_per_pulse)
                                                : 1;
                                            return (pulses * litresPerPulse) / 1000;
                                        },
                                        decimals: 3,
                                        // Distinguishes the water-meter detail
                                        // from the door-counter detail in the
                                        // chart header at a glance.
                                        titleIcon: <WaterDropIcon size={18} />,
                                    } : {})}
                                />
                            ) : isAiMeterReader ? (
                                <AiMeterReaderDetail
                                    sensor={sensor}
                                    selectedTimezone={selectedTimezone}
                                    isModal={isModal}
                                    cardStyle={cardStyle}
                                    dateFormat={dateFormat}
                                    onDateRangeChange={handleDoorDateRangeChange}
                                    timelineData={timelineData}
                                    timelineColumns={timelineColumns}
                                />
                            ) : (
                                /* Regular sensor display */
                                <Row gutter={[isModal ? 16 : 24, isModal ? 16 : 24]}>
                                    {/* Current Values Section */}
                                    <Col span={24}>
                                        <Card title="Current Values" style={cardStyle}>
                                            <Row gutter={[isModal ? 12 : 16, isModal ? 12 : 16]}>
                                                {sensorTypes.map(sensorType => {
                                                    const value = getLastKnownValue(sensor, sensorType);
                                                    return (
                                                        <Col
                                                            xs={24}
                                                            sm={isModal ? 24 : 12}
                                                            lg={isModal ? 12 : 6}
                                                            key={sensorType}
                                                        >
                                                            <Card
                                                                size="small"
                                                                hoverable
                                                                className={selectedSensorType === sensorType ? 'selected-sensor-type' : ''}
                                                                onClick={() => setSelectedSensorType(sensorType)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    border: selectedSensorType === sensorType ? '2px solid #1890ff' : undefined
                                                                }}
                                                            >
                                                                <div style={{ textAlign: 'center' }}>
                                                                    <div style={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'flex-start',
                                                                        marginBottom: isModal ? '6px' : '8px'
                                                                    }}>
                                                                        <div style={{ fontSize: isModal ? '18px' : '24px' }}>
                                                                            {getSensorTypeIcon(sensorType)}
                                                                        </div>
                                                                        <Button
                                                                            size="small"
                                                                            shape="circle"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleEditSensor(sensor, sensorType, 'alarm');
                                                                            }}
                                                                            style={{ fontSize: '12px' }}
                                                                        >
                                                                            <BellOutlined style={{
                                                                                color: '#0B2A1B',
                                                                                fontSize: isModal ? '12px' : '14px',
                                                                            }}></BellOutlined>
                                                                        </Button>
                                                                    </div>
                                                                    <Text strong style={{ fontSize: isModal ? '12px' : '14px' }}>
                                                                        {capitalizeFirstLetter(sensorType)}
                                                                    </Text>
                                                                    <div style={{ marginTop: isModal ? '6px' : '8px' }}>
                                                                        {value ? (
                                                                            <>
                                                                                <Title
                                                                                    level={isModal ? 4 : 3}
                                                                                    style={{
                                                                                        margin: 0,
                                                                                        color: '#0B2A1B',
                                                                                        fontSize: isModal ? '16px' : undefined
                                                                                    }}
                                                                                >
                                                                                    {value.value}{value.unit}
                                                                                </Title>
                                                                                <Text
                                                                                    type="secondary"
                                                                                    style={{ fontSize: isModal ? '10px' : '12px' }}
                                                                                >
                                                                                    {moment(value.time, 'DD-MM-YYYY HH:mm').isValid()
                                                                                        ? moment(value.time, 'DD-MM-YYYY HH:mm').format('DD/MM HH:mm')
                                                                                        : value.time
                                                                                    }
                                                                                </Text>
                                                                            </>
                                                                        ) : (
                                                                            <Text type="secondary">No data</Text>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </Card>
                                                        </Col>
                                                    );
                                                })}
                                            </Row>
                                        </Card>
                                    </Col>

                                    {/* Timeline Chart - Conditional display for modal */}
                                    {selectedSensorType && (
                                        <Col span={24}>
                                            <Card
                                                title={`${capitalizeFirstLetter(selectedSensorType)} Timeline Chart`}
                                                style={cardStyle}
                                            >
                                                {renderTimelineChart(sensor, selectedSensorType)}
                                            </Card>
                                        </Col>
                                    )}

                                    {/* Statistics - Adjusted layout for modal */}
                                    {selectedSensorType && (
                                        <Col span={24}>
                                            <Row gutter={[isModal ? 16 : 24, isModal ? 16 : 24]}>
                                                {/* Statistics Chart */}
                                                {sensor.stats && sensor.stats[selectedSensorType] && !isModal && (
                                                    <Col xs={24} lg={16}>
                                                        <Card title={`${capitalizeFirstLetter(selectedSensorType)} Statistics Chart`}>
                                                            {renderStatsChart(sensor, selectedSensorType)}
                                                        </Card>
                                                    </Col>
                                                )}

                                                {/* Statistics Values */}
                                                <Col xs={24} lg={
                                                    isModal ? 24 : (sensor.stats && sensor.stats[selectedSensorType] ? 8 : 24)
                                                }>
                                                    <Card title={`${capitalizeFirstLetter(selectedSensorType)} Statistics`}>
                                                        {(() => {
                                                            const stats = getSensorTypeStats(sensor, selectedSensorType);
                                                            const lastValue = getLastKnownValue(sensor, selectedSensorType);

                                                            if (!stats) {
                                                                return <Text type="secondary">No statistical data available</Text>;
                                                            }

                                                            return (
                                                                <Row gutter={[isModal ? 12 : 16, isModal ? 12 : 16]}>
                                                                    <Col span={isModal ? 12 : 24}>
                                                                        <Statistic
                                                                            title="Current"
                                                                            value={lastValue?.value || 'N/A'}
                                                                            suffix={lastValue?.unit || ''}
                                                                            precision={2}
                                                                        />
                                                                    </Col>
                                                                    <Col span={isModal ? 12 : 24}>
                                                                        <Statistic
                                                                            title="Minimum"
                                                                            value={stats.min}
                                                                            suffix={lastValue?.unit || ''}
                                                                            precision={2}
                                                                        />
                                                                    </Col>
                                                                    <Col span={isModal ? 12 : 24}>
                                                                        <Statistic
                                                                            title="Average"
                                                                            value={stats.avg}
                                                                            suffix={lastValue?.unit || ''}
                                                                            precision={2}
                                                                        />
                                                                    </Col>
                                                                    <Col span={isModal ? 12 : 24}>
                                                                        <Statistic
                                                                            title="Maximum"
                                                                            value={stats.max}
                                                                            suffix={lastValue?.unit || ''}
                                                                            precision={2}
                                                                        />
                                                                    </Col>
                                                                </Row>
                                                            );
                                                        })()}
                                                    </Card>
                                                </Col>

                                                {/* Statistics Chart for modal - separate row */}
                                                {sensor.stats && sensor.stats[selectedSensorType] && isModal && (
                                                    <Col span={24}>
                                                        <Card title={`${capitalizeFirstLetter(selectedSensorType)} Statistics Chart`}>
                                                            {renderStatsChart(sensor, selectedSensorType)}
                                                        </Card>
                                                    </Col>
                                                )}
                                            </Row>
                                        </Col>
                                    )}

                                    {/* Timeline Table */}
                                    <Col span={24}>
                                        <Card title="Recent Data">
                                            <Table
                                                dataSource={timelineData}
                                                columns={timelineColumns}
                                                pagination={{
                                                    pageSize: isModal ? 5 : 10,
                                                    showSizeChanger: !isModal,
                                                    showQuickJumper: !isModal,
                                                    showTotal: (total, range) =>
                                                        `${range[0]}-${range[1]} of ${total} readings`
                                                }}
                                                scroll={{ x: true }}
                                                size="small"
                                            />
                                        </Card>
                                    </Col>
                                </Row>
                            )}
                        </div>
                    </Spin>
                </Box>
            </SensorDetailPage>

            <EditSensorNameModal
                visible={editSensorNameModalVisible}
                sensor={editSensor}
                statsSensor={statsSensor}
                usersProperty={usersProperty}
                sensorType={clickedSensorType}
                typeModalSensor={typeModalSensor}
                onEditSensorNameSaved={onEditSensorNameSaved}
                onCancel={cancelEditSensorNameModal}
            />
        </LayoutWrapper>
    );
};

export default SensorDetail;